/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
    Tooltip,
    alpha,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { useSelector as useReduxSelector, dispatch as reduxDispatch } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import SourceLeadPopup from './SourceLeadPopup';

const TABLE_NAME = 'ROI Table';

const ALL_COLUMNS = [
    { field: 'name', label: 'Source / Campaign' },
    { field: 'leads', label: 'Leads' },
    { field: 'cost', label: 'Cost' },
    { field: 'revenue', label: 'Revenue' },
    { field: 'costPerLead', label: 'Cost Per Lead' },
    { field: 'roi', label: 'ROI' },
];

export default function ROITable({ data, title, practiceId, dateRange }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortModel, setSortModel] = useState([{ field: 'roi', sort: 'desc' }]);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    // Popup state
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupConfig, setPopupConfig] = useState({
        sourceType: null,
        title: '',
    });

    const handleLeadsClick = useCallback((row) => {
        const value = row.leads;
        if (!value || value <= 0 || !practiceId) return;
        setPopupConfig({
            sourceType: row.name,
            title: `${row.name} — All Leads`,
        });
        setPopupOpen(true);
    }, [practiceId]);

    const handleClosePopup = useCallback(() => {
        setPopupOpen(false);
    }, []);

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            reduxDispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        reduxDispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tableData) && tableData.length > 0) {
            reduxDispatch(handleTablePreferencesEdit({ ...payload, tableId: tableData[0].id }));
        } else {
            reduxDispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tableData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    // Whether filter is active
    const isFilterActive = hiddenColumns.length > 0;

    // Toggle column visibility
    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `£${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `£${(value / 1000).toFixed(1)}K`;
        }
        return `£${value?.toFixed(0) || 0}`;
    };

    const calculateROI = (revenue, cost) => {
        if (!cost || cost === 0) return 0;
        return parseFloat((((revenue - cost) / cost) * 100).toFixed(1));
    };

    // Process data to add calculated fields
    const processedData = useMemo(() =>
        data.map((row, index) => ({
            id: row.name || index,
            name: row.name,
            leads: row.leads || 0,
            cost: row.cost || 0,
            revenue: row.revenue || 0,
            costPerLead: row.leads > 0 ? row.cost / row.leads : 0,
            roi: calculateROI(row.revenue, row.cost),
        })),
        [data]
    );

    // Filter data based on search
    const filteredData = useMemo(() => {
        if (!searchQuery) return processedData;
        return processedData.filter((row) =>
            row.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [processedData, searchQuery]);

    // Export CSV
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!processedData || processedData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const rows = processedData.map((p) => visibleCols.map((col) => {
            if (col.field === 'cost' || col.field === 'revenue' || col.field === 'costPerLead') {
                return formatCurrency(p[col.field] || 0);
            }
            if (col.field === 'roi') {
                return `${p[col.field]}%`;
            }
            return p[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('roi-comparison', dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [processedData, hiddenColumns]);

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Source / Campaign',
            flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ pl: 1 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'leads',
            headerName: 'Leads',
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const clickable = value > 0 && practiceId;
                return (
                    <Typography
                        onClick={clickable ? (e) => { e.stopPropagation(); handleLeadsClick(params.row); } : undefined}
                        sx={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: clickable ? '#3b82f6' : 'inherit',
                            cursor: clickable ? 'pointer' : 'default',
                            '&:hover': clickable ? { textDecoration: 'underline' } : {},
                        }}
                    >
                        {value.toLocaleString()}
                    </Typography>
                );
            },
        },
        {
            field: 'cost',
            headerName: 'Cost',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#64748b' }}>
                    {formatCurrency(params.value)}
                </Typography>
            ),
        },
        {
            field: 'revenue',
            headerName: 'Revenue',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10b981' }}>
                    {formatCurrency(params.value)}
                </Typography>
            ),
        },
        {
            field: 'costPerLead',
            headerName: 'Cost Per Lead',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                    {formatCurrency(params.value)}
                </Typography>
            ),
        },
        {
            field: 'roi',
            headerName: 'ROI',
            flex: 1,
            renderCell: (params) => {
                const isPositive = params.value >= 0;
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            color: isPositive ? '#10b981' : '#ef4444',
                            width: '100%',
                            pr: 2,
                        }}
                    >
                        <Iconify
                            icon={isPositive ? 'eva:trending-up-fill' : 'eva:trending-down-fill'}
                            width={18}
                            height={18}
                        />
                        <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
                            {params.value}%
                        </Typography>
                    </Box>
                );
            },
        },
    ], [practiceId, handleLeadsClick]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            {/* Header with Search and Toolbar */}
            <Box
                sx={{
                    p: '20px 24px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                        {title}
                    </Typography>
                    <Tooltip title="Shows return on investment for each source/campaign based on cost and revenue data" arrow>
                        <IconButton size="small" sx={{ color: '#94a3b8' }}>
                            <Iconify icon="eva:info-outline" width={18} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search source..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* DataGrid */}
            <Box>
                <DataGridPro
                    rows={filteredData}
                    columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    columnVisibilityModel={columnVisibilityModel}
                    autoHeight
                    disableRowSelectionOnClick
                    disableColumnMenu={false}
                    pagination
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            fontSize: '13px',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                        },
                    }}
                />
            </Box>

            {data.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No ROI data available
                    </Typography>
                </Box>
            )}

            {/* Lead detail popup */}
            <SourceLeadPopup
                open={popupOpen}
                onClose={handleClosePopup}
                practiceId={practiceId}
                sourceType={popupConfig.sourceType}
                status="All"
                statusLabel="All Leads"
                dateRange={dateRange}
                title={popupConfig.title}
            />
        </Box>
    );
}

ROITable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            leads: PropTypes.number,
            cost: PropTypes.number,
            revenue: PropTypes.number,
        })
    ).isRequired,
    title: PropTypes.string,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateRange: PropTypes.shape({
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
    }),
};

ROITable.defaultProps = {
    title: 'ROI Comparison',
    practiceId: null,
    dateRange: null,
};
